/* Nova Mobile V2 Milestone 1: private compact project-state preview. */
(function () {
  'use strict';

  var gate = window.NovaMobileReleaseGate;
  if (!gate || !gate.isNovaPhoneViewport() || !gate.mobileV2Requested()) return;

  if (window.NovaMobileV2) {
    gate.record('mobile-v2-activation-rejected', { reason: 'MOBILE-V2-DUPLICATE-OWNER' });
    return;
  }

  var script = document.currentScript;
  var ROOT = document.documentElement;
  var shell = null;
  var canvas = null;
  var unsubscribe = null;
  var listeners = [];
  var renderFrame = 0;
  var renderToken = 0;
  var imageCache = Object.create(null);
  var svgCache = Object.create(null);
  var objectUrls = [];
  var active = false;
  var initializationError = '';
  var lastSnapshot = null;
  var lastRender = null;
  var metrics = {
    ownerCount: 1,
    canvasCount: 0,
    duplicateOwnerCount: 0,
    duplicateCanvasCount: 0,
    listenerCount: 0,
    observerCount: 0,
    animationFrameLoopCount: 0,
    storageWriteCount: 0,
    blankShellCount: 0,
    variantMismatchCount: 0,
    colorMismatchCount: 0,
    sizeMismatchCount: 0,
    garmentSourceMismatchCount: 0,
    unrequestedPurpleCount: 0,
    legacyEditorInitializedCount: 0,
    fallbackMissingCount: 0,
    desktopLeakageCount: 0,
    staleCallbackCount: 0,
    renderCount: 0,
    teardownCount: 0
  };

  var SURFACES = {
    'full-front': { label: 'Full Front', view: 'front', box: [0.31, 0.22, 0.38, 0.50] },
    'full-back': { label: 'Full Back', view: 'back', box: [0.31, 0.20, 0.38, 0.52] },
    'left-chest': { label: 'Left Chest', view: 'front', box: [0.54, 0.25, 0.14, 0.16] },
    'right-chest': { label: 'Right Chest', view: 'front', box: [0.32, 0.25, 0.14, 0.16] },
    'left-sleeve': { label: 'Left Sleeve', view: 'front', box: [0.70, 0.27, 0.12, 0.18] },
    'right-sleeve': { label: 'Right Sleeve', view: 'front', box: [0.18, 0.27, 0.12, 0.18] },
    nape: { label: 'Nape', view: 'back', box: [0.43, 0.15, 0.14, 0.10] }
  };

  function record(type, detail) {
    gate.record(type, detail || {});
  }

  function anomaly(code, detail) {
    var counter = {
      'MOBILE-V2-DUPLICATE-OWNER': 'duplicateOwnerCount',
      'MOBILE-V2-DUPLICATE-CANVAS': 'duplicateCanvasCount',
      'MOBILE-V2-VARIANT-MISMATCH': 'variantMismatchCount',
      'MOBILE-V2-COLOR-MISMATCH': 'colorMismatchCount',
      'MOBILE-V2-SIZE-MISMATCH': 'sizeMismatchCount',
      'MOBILE-V2-GARMENT-SOURCE-MISMATCH': 'garmentSourceMismatchCount',
      'MOBILE-V2-UNREQUESTED-PURPLE': 'unrequestedPurpleCount',
      'MOBILE-V2-LEGACY-EDITOR-INITIALIZED': 'legacyEditorInitializedCount',
      'MOBILE-V2-FALLBACK-MISSING': 'fallbackMissingCount',
      'MOBILE-V2-DESKTOP-LEAKAGE': 'desktopLeakageCount',
      'MOBILE-V2-BLANK-SHELL': 'blankShellCount'
    }[code];
    if (counter) metrics[counter] += 1;
    record('mobile-v2-anomaly', Object.assign({ code: code }, detail || {}));
  }

  function addListener(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    listeners.push({ target: target, type: type, handler: handler, options: options });
    metrics.listenerCount = listeners.length;
  }

  function removeListeners() {
    listeners.forEach(function (entry) {
      entry.target.removeEventListener(entry.type, entry.handler, entry.options);
    });
    listeners = [];
    metrics.listenerCount = 0;
  }

  function clean(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  }

  function finite(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (imageCache[url]) return imageCache[url];
    imageCache[url] = new Promise(function (resolve, reject) {
      var image = new Image();
      image.decoding = 'async';
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error('Unable to load Mobile V2 image source.')); };
      image.src = url;
    });
    return imageCache[url];
  }

  function fetchGarment(view, color) {
    var assetUrl = script && script.getAttribute(
      view === 'back' ? 'data-nova-back-svg-url' : 'data-nova-front-svg-url'
    );
    var cacheKey = [assetUrl, color && color.svgFill || ''].join('|');
    if (svgCache[cacheKey]) return svgCache[cacheKey];
    svgCache[cacheKey] = window.fetch(assetUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Unable to load the authoritative garment source.');
        return response.text();
      })
      .then(function (source) {
        var documentSource = new DOMParser().parseFromString(source, 'image/svg+xml');
        var svg = documentSource.documentElement;
        if (!svg || String(svg.nodeName).toLowerCase() !== 'svg') {
          throw new Error('The authoritative garment source is not valid SVG.');
        }
        var fill = color && color.svgFill;
        if (fill) {
          Array.prototype.slice.call(svg.querySelectorAll(
            '#garment-base > .garment-base, #garment-base .garment-base, .garment-base, [data-nova-garment-panel="true"]'
          )).forEach(function (panel) {
            panel.setAttribute('fill', fill);
            panel.style.setProperty('fill', fill);
          });
        }
        var viewBox = String(svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
        var width = viewBox.length === 4 && viewBox[2] > 0 ? viewBox[2] : finite(svg.getAttribute('width'), 1000);
        var height = viewBox.length === 4 && viewBox[3] > 0 ? viewBox[3] : finite(svg.getAttribute('height'), 1000);
        var blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
        var objectUrl = URL.createObjectURL(blob);
        objectUrls.push(objectUrl);
        return loadImage(objectUrl).then(function (image) {
          return { image: image, objectUrl: objectUrl, sourceWidth: width, sourceHeight: height, assetUrl: assetUrl };
        });
      });
    return svgCache[cacheKey];
  }

  function surfaceRecord(snapshot) {
    var store = snapshot && snapshot.surfaceStore;
    var id = clean(snapshot && snapshot.activeSurfaceId) ||
      clean(store && store.selectedSurfaceIds && store.selectedSurfaceIds[0]) ||
      'full-front';
    if (!SURFACES[id]) id = 'full-front';
    return {
      id: id,
      spec: SURFACES[id],
      state: store && store.surfaces && store.surfaces[id] || { artworkLayers: [], textState: { layers: [] } }
    };
  }

  function coordinate(value, length) {
    var number = finite(value, 0.5);
    return (Math.abs(number) <= 1.5 ? number : number / 100) * length;
  }

  function containRect(containerWidth, containerHeight, sourceWidth, sourceHeight) {
    var inset = Math.max(12, Math.min(containerWidth, containerHeight) * 0.035);
    var availableWidth = Math.max(1, containerWidth - inset * 2);
    var availableHeight = Math.max(1, containerHeight - inset * 2);
    var scale = Math.min(availableWidth / sourceWidth, availableHeight / sourceHeight);
    var width = sourceWidth * scale;
    var height = sourceHeight * scale;
    return {
      left: (containerWidth - width) / 2,
      top: (containerHeight - height) / 2,
      width: width,
      height: height,
      scale: scale
    };
  }

  function printRect(surface, garmentRect, sourceWidth, sourceHeight) {
    var reference = surface.state && surface.state.sceneReference;
    var model = reference && reference.placementModel;
    if (
      model && finite(model.width, 0) > 0 && finite(model.height, 0) > 0 &&
      sourceWidth > 0 && sourceHeight > 0
    ) {
      return {
        left: garmentRect.left + finite(model.x, 0) / sourceWidth * garmentRect.width,
        top: garmentRect.top + finite(model.y, 0) / sourceHeight * garmentRect.height,
        width: finite(model.width, 1) / sourceWidth * garmentRect.width,
        height: finite(model.height, 1) / sourceHeight * garmentRect.height
      };
    }
    var box = surface.spec.box;
    return {
      left: garmentRect.left + garmentRect.width * box[0],
      top: garmentRect.top + garmentRect.height * box[1],
      width: garmentRect.width * box[2],
      height: garmentRect.height * box[3]
    };
  }

  function artworkSource(layer) {
    return clean(
      layer && (
        layer.processedDataUrl ||
        layer.dataUrl ||
        layer.originalDataUrl ||
        layer.previewUrl ||
        layer.originalPreviewUrl
      )
    );
  }

  function drawArtwork(context, layer, area, token) {
    var source = artworkSource(layer);
    if (!source) {
      context.save();
      context.strokeStyle = 'rgba(29,22,100,.5)';
      context.setLineDash([5, 4]);
      context.strokeRect(area.left + area.width * 0.25, area.top + area.height * 0.35, area.width * 0.5, area.height * 0.3);
      context.fillStyle = '#1d1664';
      context.font = '600 10px Arial, sans-serif';
      context.textAlign = 'center';
      context.fillText(clean(layer && layer.fileName) || 'Saved artwork', area.left + area.width / 2, area.top + area.height / 2 + 4);
      context.restore();
      return Promise.resolve({ rendered: true, placeholder: true });
    }
    return loadImage(source).then(function (image) {
      if (token !== renderToken) return { rendered: false, stale: true };
      var aspect = finite(layer.aspect, image.naturalWidth / Math.max(1, image.naturalHeight));
      var scaleX = Math.max(0.04, finite(layer.scaleX, finite(layer.scale, 0.55)));
      var scaleY = Math.max(0.04, finite(layer.scaleY, finite(layer.scale, 0.55)));
      var width = area.width * Math.min(1.8, scaleX);
      var height = width / Math.max(0.05, aspect) * Math.min(1.8, scaleY / Math.max(0.04, scaleX));
      var x = area.left + coordinate(layer.x, area.width);
      var y = area.top + coordinate(layer.y, area.height);
      context.save();
      context.translate(x, y);
      context.rotate(finite(layer.rotation, 0) * Math.PI / 180);
      context.globalAlpha = Math.max(0, Math.min(1, finite(layer.opacity, 1)));
      context.drawImage(image, -width / 2, -height / 2, width, height);
      context.restore();
      return { rendered: true, placeholder: false };
    });
  }

  function wrapLines(context, text, maximumWidth) {
    var lines = [];
    String(text || '').replace(/\r\n?/g, '\n').split('\n').forEach(function (paragraph) {
      var words = paragraph.split(/\s+/);
      var line = '';
      words.forEach(function (word) {
        var next = line ? line + ' ' + word : word;
        if (line && context.measureText(next).width > maximumWidth) {
          lines.push(line);
          line = word;
        } else {
          line = next;
        }
      });
      lines.push(line);
    });
    return lines.length ? lines : [''];
  }

  function drawText(context, layer, area) {
    var text = String(layer && (layer.text || layer.value) || '');
    if (!text) return false;
    var x = area.left + coordinate(layer.x, area.width);
    var y = area.top + coordinate(layer.y, area.height);
    var sourceFont = Math.max(10, finite(layer.fontSize, 28));
    var fontSize = Math.max(9, Math.min(area.height * 0.28, sourceFont * area.width / 520));
    var family = clean(layer.fontFamily) || 'Arial';
    context.save();
    context.translate(x, y);
    context.rotate(finite(layer.rotation, 0) * Math.PI / 180);
    context.scale(
      Math.max(0.1, finite(layer.scaleX, 1)),
      Math.max(0.1, finite(layer.scaleY, 1))
    );
    context.globalAlpha = Math.max(0, Math.min(1, finite(layer.opacity, 1)));
    context.fillStyle = clean(layer.color) || '#111111';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = (layer.italic ? 'italic ' : '') + (layer.bold ? '700 ' : '400 ') + fontSize + 'px ' + family;
    var lines = wrapLines(context, text, area.width * 0.92);
    var lineHeight = fontSize * Math.max(0.8, finite(layer.lineSpacing, 1.15));
    lines.forEach(function (line, index) {
      context.fillText(line, 0, (index - (lines.length - 1) / 2) * lineHeight, area.width * 0.98);
    });
    context.restore();
    return true;
  }

  function updateFacts(snapshot, surface) {
    if (!shell) return;
    var variant = snapshot && snapshot.variant;
    var total = Object.keys(snapshot && snapshot.quantities || {}).reduce(function (sum, key) {
      return sum + (parseInt(snapshot.quantities[key], 10) || 0);
    }, 0);
    var methodLabels = { dtf: 'Full Color DTF', screen_print: 'Screen Printing', embroidery: 'Embroidery' };
    var values = {
      location: surface.spec.label,
      color: variant && variant.shopifyColorValue || 'Current Shopify selection',
      total: total ? total + (total === 1 ? ' piece' : ' pieces') : 'No quantity selected',
      decoration: methodLabels[snapshot && snapshot.decorationMethod] || clean(snapshot && snapshot.decorationMethod) || 'Not selected',
      variant: variant ? variant.selectedVariantId + ' · ' + variant.sizeOptionValue : 'Unavailable',
      source: variant && String(variant.garmentSource || '').replace('{view}', surface.spec.view) || 'Unavailable'
    };
    Object.keys(values).forEach(function (key) {
      var node = shell.querySelector('[data-nova-mobile-v2-' + key + ']');
      if (node) node.textContent = values[key];
    });
  }

  function validateVariant(snapshot) {
    var variant = snapshot && snapshot.variant;
    if (!variant) {
      anomaly('MOBILE-V2-VARIANT-MISMATCH', { reason: 'missing-active-variant' });
      return false;
    }
    var input = Array.prototype.slice.call(document.querySelectorAll('[data-ts-bulk-qty][data-variant-id]')).find(function (candidate) {
      return String(candidate.getAttribute('data-variant-id') || '') === variant.selectedVariantId;
    });
    if (!input) {
      anomaly('MOBILE-V2-VARIANT-MISMATCH', { variantId: variant.selectedVariantId, reason: 'variant-input-missing' });
      return false;
    }
    if (String(input.getAttribute('data-color') || '') !== variant.shopifyColorValue) {
      anomaly('MOBILE-V2-COLOR-MISMATCH', {
        variantId: variant.selectedVariantId,
        expected: String(input.getAttribute('data-color') || ''),
        actual: variant.shopifyColorValue
      });
    }
    if (String(input.getAttribute('data-size') || '') !== variant.sizeOptionValue) {
      anomaly('MOBILE-V2-SIZE-MISMATCH', {
        variantId: variant.selectedVariantId,
        expected: String(input.getAttribute('data-size') || ''),
        actual: variant.sizeOptionValue
      });
    }
    var saved = snapshot.savedProject || {};
    if (
      variant.shopifyColorValue === 'Purple Rush' &&
      String(saved.selectedVariantId || '') !== variant.selectedVariantId
    ) anomaly('MOBILE-V2-UNREQUESTED-PURPLE', { variantId: variant.selectedVariantId });
    return true;
  }

  function render(snapshot) {
    if (!active || !shell || !canvas) return Promise.resolve(false);
    var token = ++renderToken;
    var surface = surfaceRecord(snapshot);
    var variant = snapshot && snapshot.variant;
    updateFacts(snapshot, surface);
    validateVariant(snapshot);
    var stage = shell.querySelector('[data-nova-mobile-v2-stage]');
    var status = shell.querySelector('[data-nova-mobile-v2-stage-status]');
    var rect = stage && stage.getBoundingClientRect();
    if (!rect || rect.width < 40 || rect.height < 80 || !variant) {
      if (status) status.textContent = 'Waiting for the saved project state';
      anomaly('MOBILE-V2-BLANK-SHELL', { width: rect && rect.width || 0, height: rect && rect.height || 0 });
      return Promise.resolve(false);
    }
    var cssWidth = Math.max(1, Math.round(rect.width));
    var cssHeight = Math.max(1, Math.round(rect.height));
    var dpr = Math.max(1, Math.min(4, Number(window.devicePixelRatio) || 1));
    canvas.width = Math.round(cssWidth * dpr);
    canvas.height = Math.round(cssHeight * dpr);
    var context = canvas.getContext('2d', { alpha: true });
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    context.clearRect(0, 0, cssWidth, cssHeight);
    if (status) status.textContent = 'Preparing project preview…';

    var garmentSource = String(variant.garmentSource || '').replace('{view}', surface.spec.view);
    var garmentAssetUrl = script && script.getAttribute(
      surface.spec.view === 'back' ? 'data-nova-back-svg-url' : 'data-nova-front-svg-url'
    );
    var garmentAssetName = String(garmentAssetUrl || '').split('?')[0].split('/').pop();
    if (garmentSource !== garmentAssetName) {
      anomaly('MOBILE-V2-GARMENT-SOURCE-MISMATCH', {
        expected: garmentSource,
        actual: garmentAssetName
      });
    }

    return fetchGarment(surface.spec.view, variant).then(function (garment) {
      if (token !== renderToken) {
        metrics.staleCallbackCount += 1;
        record('mobile-v2-stale-variant-callback-rejected', { renderToken: token, currentToken: renderToken });
        return false;
      }
      var garmentRect = containRect(cssWidth, cssHeight, garment.sourceWidth, garment.sourceHeight);
      context.drawImage(garment.image, garmentRect.left, garmentRect.top, garmentRect.width, garmentRect.height);
      var area = printRect(surface, garmentRect, garment.sourceWidth, garment.sourceHeight);
      var artwork = Array.isArray(surface.state.artworkLayers) ? surface.state.artworkLayers.slice() : [];
      var textLayers = surface.state.textState && Array.isArray(surface.state.textState.layers)
        ? surface.state.textState.layers.slice()
        : [];
      var ordered = artwork.map(function (layer, index) {
        return { type: 'artwork', layer: layer, index: index };
      }).concat(textLayers.map(function (layer, index) {
        return { type: 'text', layer: layer, index: index };
      })).sort(function (left, right) {
        var stackDifference = finite(left.layer.stackLevel, 0) - finite(right.layer.stackLevel, 0);
        if (stackDifference) return stackDifference;
        var orderDifference = finite(left.layer.layerOrder, left.index) - finite(right.layer.layerOrder, right.index);
        if (orderDifference) return orderDifference;
        return left.type === right.type ? left.index - right.index : left.type === 'artwork' ? -1 : 1;
      });
      return ordered.reduce(function (chain, entry) {
        return chain.then(function () {
          return entry.type === 'artwork'
            ? drawArtwork(context, entry.layer, area, token)
            : drawText(context, entry.layer, area);
        });
      }, Promise.resolve()).then(function () {
        if (token !== renderToken) return false;
        metrics.renderCount += 1;
        lastRender = {
          token: token,
          variantId: variant.selectedVariantId,
          color: variant.shopifyColorValue,
          size: variant.sizeOptionValue,
          garmentSource: garmentSource,
          activeSurfaceId: surface.id,
          artworkLayerCount: artwork.length,
          textLayerCount: textLayers.length,
          canvasCssWidth: cssWidth,
          canvasCssHeight: cssHeight,
          canvasBackingWidth: canvas.width,
          canvasBackingHeight: canvas.height,
          devicePixelRatio: dpr
        };
        if (status) status.textContent = 'Project preview';
        shell.setAttribute('data-nova-mobile-v2-render-state', 'ready');
        record('mobile-v2-canvas-rendered', Object.assign({}, lastRender));
        return true;
      });
    });
  }

  function scheduleRender(snapshot) {
    lastSnapshot = snapshot || gate.getStateSnapshot('mobile-v2-scheduled-render');
    if (renderFrame) return;
    renderFrame = window.requestAnimationFrame(function () {
      renderFrame = 0;
      render(lastSnapshot).catch(failInitialization);
    });
  }

  function buildShell() {
    if (document.querySelectorAll('[data-nova-mobile-v2-shell]').length) {
      anomaly('MOBILE-V2-DUPLICATE-CANVAS', { reason: 'shell-already-present' });
      throw new Error('MOBILE-V2-DUPLICATE-OWNER');
    }
    var section = document.querySelector('[data-nova-product-id]');
    var title = clean(section && section.getAttribute('data-nova-product-title')) || 'Twisted Swag Design Studio';
    shell = document.createElement('section');
    shell.className = 'nova-mobile-v2-shell';
    shell.setAttribute('data-nova-mobile-v2-shell', '');
    shell.setAttribute('data-nova-mobile-v2-render-state', 'preparing');
    shell.setAttribute('aria-label', 'Private project preview');
    shell.innerHTML =
      '<header class="nova-mobile-v2-shell__header">' +
        '<div><span class="nova-mobile-v2-shell__eyebrow">Project Preview</span><strong class="nova-mobile-v2-shell__title">' + escapeHtml(title) + '</strong></div>' +
        '<button type="button" class="nova-mobile-v2-shell__close" data-nova-mobile-v2-close>Close Preview</button>' +
      '</header>' +
      '<main class="nova-mobile-v2-shell__main">' +
        '<div class="nova-mobile-v2-shell__context">' +
          '<div class="nova-mobile-v2-shell__fact"><span>Imprint location</span><strong data-nova-mobile-v2-location>Loading…</strong></div>' +
          '<div class="nova-mobile-v2-shell__fact"><span>Garment color</span><strong data-nova-mobile-v2-color>Loading…</strong></div>' +
          '<div class="nova-mobile-v2-shell__fact"><span>Total pieces</span><strong data-nova-mobile-v2-total>Loading…</strong></div>' +
          '<div class="nova-mobile-v2-shell__fact"><span>Decoration method</span><strong data-nova-mobile-v2-decoration>Loading…</strong></div>' +
        '</div>' +
        '<div class="nova-mobile-v2-shell__stage" data-nova-mobile-v2-stage>' +
          '<canvas data-nova-mobile-v2-canvas aria-label="Compact garment project preview"></canvas>' +
          '<span class="nova-mobile-v2-shell__stage-status" data-nova-mobile-v2-stage-status>Preparing saved project…</span>' +
        '</div>' +
        '<details class="nova-mobile-v2-shell__technical" data-nova-mobile-v2-technical-details>' +
          '<summary>Technical Details</summary>' +
          '<div class="nova-mobile-v2-shell__technical-body">' +
            '<span>Active variant: <strong data-nova-mobile-v2-variant>Loading…</strong></span>' +
            '<span>Garment source: <strong data-nova-mobile-v2-source>Loading…</strong></span>' +
          '</div>' +
        '</details>' +
      '</main>' +
      '<footer class="nova-mobile-v2-shell__footer">' +
        '<button type="button" class="nova-mobile-v2-shell__fallback-button" data-nova-mobile-v2-fallback>View Completion Options</button>' +
      '</footer>';
    var fallback = gate.showFallback();
    if (fallback && fallback.parentNode) fallback.parentNode.insertBefore(shell, fallback);
    else document.body.appendChild(shell);
    canvas = shell.querySelector('[data-nova-mobile-v2-canvas]');
    metrics.canvasCount = document.querySelectorAll('[data-nova-mobile-v2-canvas]').length;
    if (metrics.canvasCount !== 1) {
      anomaly('MOBILE-V2-DUPLICATE-CANVAS', { count: metrics.canvasCount });
      throw new Error('MOBILE-V2-DUPLICATE-CANVAS');
    }
    ROOT.setAttribute('data-nova-mobile-v2-active', 'true');
    record('mobile-v2-canvas-created', { count: metrics.canvasCount });
  }

  function closeToFallback() {
    teardown('return-to-product');
    var fallback = gate.showFallback();
    if (fallback) {
      fallback.scrollIntoView({ block: 'start', behavior: 'auto' });
      var action = fallback.querySelector('[data-nova-mobile-fallback-action="desktop"]');
      if (action) action.focus({ preventScroll: true });
    }
  }

  function bindShell() {
    addListener(shell.querySelector('[data-nova-mobile-v2-close]'), 'click', closeToFallback);
    addListener(shell.querySelector('[data-nova-mobile-v2-fallback]'), 'click', closeToFallback);
    addListener(window, 'resize', function () { scheduleRender(gate.getStateSnapshot('viewport-resize')); }, { passive: true });
    addListener(window, 'orientationchange', function () { scheduleRender(gate.getStateSnapshot('orientationchange')); }, { passive: true });
    addListener(window, 'pageshow', function () {
      gate.restoreAuthoritativeState({ source: 'mobile-v2-pageshow' }).then(scheduleRender);
    });
    addListener(window, 'popstate', function () {
      gate.restoreAuthoritativeState({ source: 'mobile-v2-popstate' }).then(scheduleRender);
    });
    unsubscribe = gate.subscribe(function (snapshot) {
      record('mobile-v2-state-snapshot', {
        source: snapshot.source,
        variantId: snapshot.variant && snapshot.variant.selectedVariantId || '',
        activeSurfaceId: snapshot.activeSurfaceId
      });
      scheduleRender(snapshot);
    });
  }

  function teardown(reason) {
    if (renderFrame) {
      window.cancelAnimationFrame(renderFrame);
      renderFrame = 0;
    }
    renderToken += 1;
    removeListeners();
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
    if (shell && shell.isConnected) shell.remove();
    objectUrls.forEach(function (url) { URL.revokeObjectURL(url); });
    objectUrls = [];
    imageCache = Object.create(null);
    svgCache = Object.create(null);
    shell = null;
    canvas = null;
    active = false;
    metrics.canvasCount = 0;
    metrics.teardownCount += 1;
    ROOT.removeAttribute('data-nova-mobile-v2-active');
    record('mobile-v2-owner-destroyed', { reason: String(reason || 'teardown') });
  }

  function failInitialization(error) {
    initializationError = String(error && error.message || error || 'Unknown Mobile V2 initialization error');
    record('mobile-v2-initialization-error', { message: initializationError });
    teardown('initialization-error');
    gate.showFallback();
    return false;
  }

  function initialize() {
    record('mobile-v2-activation-requested', {
      phone: gate.isNovaPhoneViewport(),
      requested: gate.mobileV2Requested()
    });
    if (!gate.isNovaPhoneViewport() || !gate.mobileV2Requested() || !gate.suppressLegacyMobile()) {
      record('mobile-v2-activation-rejected', { reason: 'feature-contract-rejected' });
      return false;
    }
    try {
      var fallback = gate.showFallback();
      if (!fallback) anomaly('MOBILE-V2-FALLBACK-MISSING', { source: 'initialize' });
      var legacyState = gate.getLegacyRuntimeState();
      if (legacyState.requested) {
        anomaly('MOBILE-V2-LEGACY-EDITOR-INITIALIZED', { source: 'initialize' });
      }
      buildShell();
      bindShell();
      active = true;
      record('mobile-v2-owner-created', { ownerCount: metrics.ownerCount });
      record('mobile-v2-activation-approved', { mode: 'private-query' });
      return gate.restoreAuthoritativeState({ source: 'mobile-v2-initialize' }).then(function (snapshot) {
        lastSnapshot = snapshot;
        scheduleRender(snapshot);
        return true;
      }, failInitialization);
    } catch (error) {
      return failInitialization(error);
    }
  }

  window.NovaMobileV2 = {
    version: 'NOVA-MOBILE-V2-MILESTONE-1.0.0',
    ownerCount: 1,
    writerCount: 1,
    activation: 'private-query-only',
    initialize: initialize,
    teardown: teardown,
    refresh: function () { scheduleRender(gate.getStateSnapshot('api-refresh')); },
    getStateSnapshot: function () {
      return lastSnapshot || gate.getStateSnapshot('api-snapshot');
    },
    getVariantRestorationOwner: function () { return 'NovaMobileV2 via NovaMobileReleaseGate'; },
    getMetrics: function () { return Object.assign({}, metrics); },
    getLastRender: function () { return lastRender ? Object.assign({}, lastRender) : null; },
    getInitializationError: function () { return initializationError; },
    isActive: function () { return active; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
